/**
 * Ein Gerät nach einem unbekannten Scan anlegen (ADR 0026).
 *
 * Zwei Schritte: Der Server legt die Stammdaten an -- nur mit Netz, wie beim
 * Packstück --, und die Einbuchung geht danach durch die Warteschlange wie
 * jede andere Buchung (CLAUDE.md §3.3). So zeigt die Oberfläche den Ort
 * sofort.
 */

import type { Artikel, GeraetErfassen } from "@thw/contracts";
import { anfrage, sitzungLesen } from "../api/klient.js";
import { artikelUebernehmen } from "./abgleich.js";
import { bestandLaden, type Bestand } from "./bestand.js";
import { entfernen } from "./nachzuerfassen.js";
import { standardziel, verschieben, type Gebucht } from "./verschieben.js";
import { zieleLesen } from "./ziele.js";
import type { Ziel } from "@thw/core-domain";

/** Mehr Vorschläge liest mit Handschuhen niemand. */
const HOECHSTENS = 5;

/**
 * Bezeichnungen aus dem Bestand, die zum Getippten passen -- vorn
 * beginnende zuerst. Hält die Namen einheitlich und spart Tippen.
 */
export function bezeichnungen(bestand: Bestand, eingabe: string): string[] {
  const text = eingabe.trim().toLocaleLowerCase("de");
  if (text.length < 2) return [];
  const vorn = new Set<string>();
  const drin = new Set<string>();
  for (const a of bestand.artikel.values()) {
    if (a.art === "packstueck") continue;
    const name = a.bezeichnung.toLocaleLowerCase("de");
    if (name === text) continue;
    if (name.startsWith(text)) vorn.add(a.bezeichnung);
    else if (name.includes(text)) drin.add(a.bezeichnung);
  }
  const sortiert = (s: Set<string>): string[] => [...s].sort((x, y) => x.localeCompare(y, "de"));
  return [...sortiert(vorn), ...sortiert(drin)].slice(0, HOECHSTENS);
}

/**
 * Wo das neue Gerät eingebucht wird, wenn niemand etwas anderes wählt: der
 * Behälter, in dem man gerade ist, sonst das zuletzt benutzte Ziel, sonst
 * „Ohne festen Platz“.
 */
export function vorbelegterOrt(bestand: Bestand, ort_id: string | null): Ziel | null {
  if (ort_id !== null) {
    const ort = bestand.lagerorte.get(ort_id);
    if (ort !== undefined && ort.aktiv && !ort.unbekannt) return { art: "lagerort", id: ort.id };
  }
  const benutzer_id = sitzungLesen()?.benutzer.id;
  const letztes = benutzer_id === undefined ? undefined : zieleLesen(benutzer_id).letzte[0];
  if (letztes !== undefined) return letztes;
  const ohne = standardziel(bestand);
  return ohne === null ? null : { art: "lagerort", id: ohne.id };
}

export interface NeuesGeraet {
  inventarnummer: string;
  bezeichnung: string;
  /** Der gelesene Code. */
  code: string | null;
  /** Wo es eingebucht wird; `null` heißt: gar nicht. */
  ziel: Ziel | null;
}

/**
 * Legt an, übernimmt das Gerät in die lokale Kopie und bucht es ein. Ohne
 * Netz wirft es, und nichts ist angelegt.
 */
export async function geraetAnlegen(
  neu: NeuesGeraet,
): Promise<{ artikel: Artikel; gebucht: Gebucht | null }> {
  if (!globalThis.navigator.onLine) throw new Error("Ein Gerät anlegen geht nur mit Netz.");
  const eingabe: GeraetErfassen = {
    inventarnummer: neu.inventarnummer,
    bezeichnung: neu.bezeichnung.trim(),
    code: neu.code,
  };
  let artikel: Artikel;
  try {
    artikel = await anfrage<Artikel>("POST", "/api/core/geraete", eingabe, 15_000);
  } catch (fehler) {
    if (fehler instanceof TypeError || fehler instanceof DOMException) {
      throw new Error("Keine Verbindung zum Server — das Gerät ist nicht angelegt.", {
        cause: fehler,
      });
    }
    throw fehler;
  }
  await artikelUebernehmen([artikel]);
  if (neu.code !== null) await entfernen(neu.code);

  if (neu.ziel === null) return { artikel, gebucht: null };
  const gebucht = await verschieben(await bestandLaden(), artikel.id, neu.ziel);
  return { artikel, gebucht };
}
